import { Text, Checkbox, Stack, Box } from '@chakra-ui/react';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';

import { SkeletonChecklist } from 'components/Skeleton';
import { useFetchHolidayFromCalendar } from 'hooks/timeOff/useFetchHolidayFromCalendar';

import { initialImportHolidayData } from '../schema';

const HolidayList = () => {
  const { t } = useTranslation(['timeOff', 'common', 'components']);
  const { values, setFieldValue } = useFormikContext<typeof initialImportHolidayData>();

  const { data: holidaysData, status } = useFetchHolidayFromCalendar(
    { country: values.country?.value, year: values.year?.value },
    { enabled: !!values.country?.value && !!values.year?.value }
  );

  const handleIndeterminateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setFieldValue('holidays', holidaysData?.payload || []);
    } else {
      setFieldValue('holidays', []);
    }
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>, holiday: any) => {
    const holidayValues = values.holidays || [];

    if (e.target.checked) {
      setFieldValue('holidays', holidayValues.concat(holiday));
    } else {
      const updatedValue = holidayValues.filter((item: any) => item.uuid !== holiday.uuid);
      setFieldValue('holidays', updatedValue);
    }
  };

  if (status === 'loading') return <SkeletonChecklist />;

  if (Array.isArray(holidaysData?.payload) && !holidaysData?.payload.length) {
    return <Text color="gray.500">{t('components:noRecordsFound')}</Text>;
  }

  if (Array.isArray(holidaysData?.payload) && holidaysData?.payload.length) {
    return (
      <Box
        border="1px solid"
        borderColor="gray.200"
        borderRadius="lg"
        overflowY="auto"
        overflowX="auto"
        maxHeight="550px"
      >
        <Box
          borderBottom="1px solid"
          borderColor="gray.200"
          px="3"
          py="2"
          position="sticky"
          top="0"
          zIndex="1"
          background="gray.100"
          textTransform="uppercase"
          color="gray.500"
        >
          <Checkbox
            width="100%"
            colorScheme="secondary"
            sx={{ '& .chakra-checkbox__label': { fontSize: 'xs', width: '100%' } }}
            isChecked={values.holidays?.length === holidaysData.payload.length}
            onChange={handleIndeterminateChange}
          >
            <Stack direction="row" justifyContent="space-between" ml="1">
              <Text flex={{ base: '0 0 130px', md: '1 1 auto' }}>Name</Text>
              <Text flex="0 0 120px">Date</Text>
            </Stack>
          </Checkbox>
        </Box>
        {holidaysData?.payload.map((holiday: any) => (
          <Box key={holidaysData.uuid} borderBottom="1px solid" borderColor="gray.200" px="3" py="2">
            <Checkbox
              width="100%"
              colorScheme="secondary"
              sx={{ '& .chakra-checkbox__label': { fontSize: 'sm', width: '100%' } }}
              value={holiday}
              isChecked={holiday?.is_imported || values.holidays?.some((item: any) => item.uuid === holiday.uuid)}
              onChange={e => handleCheckboxChange(e, holiday)}
              isDisabled={holiday?.is_imported}
            >
              <Stack direction="row" justifyContent="space-between" ml="1">
                <Text color="primary.900" flex={{ base: '0 0 130px', md: '1 1 auto' }}>
                  {holiday.name}
                </Text>
                <Text flex="0 0 120px" fontSize="xs" color="gray.500">
                  {holiday?.formatted_date}
                </Text>
              </Stack>
            </Checkbox>
          </Box>
        ))}
      </Box>
    );
  }

  return <div>{t('common:unableToLoadData')}</div>;
};

export default HolidayList;
